import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, FileCheck2, Globe2, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { formatApiErrorDetail } from "@/app/api";
import { useAuth } from "@/app/AuthContext";
import { Footer } from "@/components/site/Footer";
import { Navbar } from "@/components/site/Navbar";
import { Seo } from "@/components/site/Seo";
import {
  JourneyChoiceGrid as ChoiceGrid,
  JourneyField as Field,
  JourneySelect as Select,
  JourneyTextInput as TextInput,
  optionLabel,
} from "@/components/journey/controls";
import {
  AWARD_OPTIONS,
  EDUCATION_OPTIONS,
  ENGLISH_OPTIONS,
  FUNDING_OPTIONS,
  LOCATION_OPTIONS,
  RIGHT_TO_WORK_OPTIONS,
  UK_STATUS_OPTIONS,
  UNKNOWN_EDUCATION_OPTION,
} from "@/components/journey/options";
import { OVERALL_LABEL, ResultPill, statusLabel } from "@/components/journey/results";
import { VerificationModal } from "@/components/journey/VerificationModal";
import { RequestCallButton } from "@/components/RequestCall";
import { EnglishAssessmentFields } from "@/components/journey/steps/EnglishAssessmentFields";
import { loadJourneySession, saveJourneySession, saveLatestEligibilityResult } from "@/lib/journey";
import {
  assessEducationAnswers,
  continueEligibilityLater,
  fetchIndividualProfile,
  fetchOpportunity,
  fetchSavedEligibilityAssessment,
  runOpportunityEligibility,
  saveEligibilityAssessment,
  startEligibilityVerification,
} from "@/lib/journeyApi";

export default function EligibilityCheck() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [opportunity, setOpportunity] = useState(null);
  const [answers, setAnswers] = useState(() => loadJourneySession());
  const [profile, setProfile] = useState(null);
  const [profileChoice, setProfileChoice] = useState(null);
  const [step, setStep] = useState(0);
  const [maxStep, setMaxStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [assessingEducation, setAssessingEducation] = useState(false);
  const [result, setResult] = useState(null);
  const [savedAssessmentId, setSavedAssessmentId] = useState("");
  const [verificationOpen, setVerificationOpen] = useState(false);
  const [error, setError] = useState("");
  const isLearner = user?.role === "individual";

  useEffect(() => {
    fetchOpportunity(slug).then(setOpportunity).catch(() => setError("Opportunity not found"));
  }, [slug]);

  useEffect(() => {
    if (!user || user.role !== "individual") return;
    fetchIndividualProfile().then(setProfile).catch(() => {});
  }, [user]);

  useEffect(() => {
    const assessmentId = searchParams.get("assessment");
    if (!assessmentId || !isLearner) return;
    fetchSavedEligibilityAssessment(assessmentId)
      .then((data) => {
        setResult(data.result);
        setAnswers(data.answers || {});
        setSavedAssessmentId(assessmentId);
        setStep(20);
        setMaxStep(20);
      })
      .catch(() => toast.error("Saved assessment could not be loaded."));
  }, [searchParams, isLearner]);

  useEffect(() => { saveJourneySession(answers); }, [answers]);
  const update = (key, value) => {
    if (result) {
      setResult(null);
      setSavedAssessmentId("");
    }
    setAnswers((current) => ({ ...current, [key]: value }));
  };
  const needsEnglishAssessment = ["needs_assessment", "not_sure", "no_current_evidence"].includes(answers.english_status);
  const steps = useMemo(() => {
    const base = ["Context", "Funding", "Status", "Education"];
    if (needsEnglishAssessment) base.push("English");
    base.push("Review", "Result");
    return base;
  }, [needsEnglishAssessment]);
  const stepName = steps[Math.min(step, steps.length - 1)];

  useEffect(() => { setMaxStep((current) => Math.max(current, step)); }, [step]);

  const canContinue = () => {
    if (stepName === "Context") return user !== undefined && (!isLearner || profileChoice !== null);
    if (stepName === "Funding") return Boolean(answers.funding_intention);
    if (stepName === "Status") return Boolean(answers.current_location && answers.uk_status);
    if (stepName === "Education") return Boolean(answers.highest_education && answers.award_status && answers.english_status);
    if (stepName === "English") return Boolean(answers.english_reading_answer && answers.english_vocabulary_answer && answers.english_grammar_answer && answers.english_writing_sample);
    return true;
  };

  const runAssessment = async (persist = false) => {
    setLoading(true);
    setError("");
    try {
      const data = await runOpportunityEligibility(slug, { answers, useProfile: profileChoice === "yes", persist });
      setResult(data);
      if (!isLearner) saveLatestEligibilityResult(data);
      setSavedAssessmentId(persist ? data.id : savedAssessmentId);
      setStep(steps.length - 1);
      setMaxStep(steps.length - 1);
      if (persist) toast.success("Eligibility assessment saved.");
      return data;
    } catch (requestError) {
      setError(formatApiErrorDetail(requestError.response?.data?.detail));
      return null;
    } finally {
      setLoading(false);
    }
  };

  const next = async () => {
    if (stepName === "Review") await runAssessment(false);
    else setStep((current) => Math.min(current + 1, steps.length - 1));
  };

  const assessEducation = async () => {
    setAssessingEducation(true);
    try {
      const data = await assessEducationAnswers(answers);
      update("education_assessment", data);
      toast.success("Potential UK level assessed.");
    } catch (requestError) {
      toast.error(formatApiErrorDetail(requestError.response?.data?.detail));
    } finally {
      setAssessingEducation(false);
    }
  };

  const saveAssessment = async () => {
    if (!result) return null;
    if (savedAssessmentId) return savedAssessmentId;
    setLoading(true);
    try {
      const data = await saveEligibilityAssessment({
        opportunity_slug: slug,
        answers,
        use_profile: profileChoice === "yes",
        result,
      });
      setSavedAssessmentId(data.id);
      toast.success("Eligibility assessment saved.");
      return data.id;
    } catch (requestError) {
      setError(formatApiErrorDetail(requestError.response?.data?.detail));
      return null;
    } finally {
      setLoading(false);
    }
  };

  const continueLater = async () => {
    if (!isLearner) {
      toast.info("Create an individual learner account to save this assessment and continue later.");
      return;
    }
    const id = await saveAssessment();
    if (!id) return;
    await continueEligibilityLater(id);
    toast.success("Assessment saved. Official verification remains required and the application is waiting for verification.");
  };

  const startVerification = async () => {
    if (isLearner) {
      const id = await saveAssessment();
      if (!id) return;
      await startEligibilityVerification(id);
    }
    setVerificationOpen(true);
  };

  if (error && !opportunity) {
    return <div className="min-h-screen bg-surface"><Navbar /><main className="mx-auto max-w-2xl px-5 pt-32"><p className="rounded-2xl bg-rose-50 p-4 text-rose-700">{error}</p></main></div>;
  }

  return (
    <div data-testid="eligibility-page" className="min-h-screen bg-surface">
      <Seo title={opportunity ? `Check eligibility — ${opportunity.title}` : "Check eligibility — OpenDegree"} description="Run an indicative, opportunity-specific OpenDegree eligibility assessment." path={`/eligibility/${slug}`} noindex />
      <Navbar />
      <main className="mx-auto max-w-6xl px-5 pb-20 pt-28 sm:px-8">
        {!opportunity ? <p className="text-slatey">Loading opportunity…</p> : (
          <>
            <div className="max-w-3xl">
              <p className="inline-flex items-center gap-2 rounded-full bg-cobalt/10 px-4 py-2 text-xs font-bold uppercase tracking-[0.18em] text-cobalt"><FileCheck2 className="h-4 w-4" /> Check eligibility</p>
              <h1 className="mt-5 font-display text-4xl font-extrabold tracking-tight text-ink sm:text-5xl">{opportunity.title}</h1>
              <p className="mt-3 text-slatey">{opportunity.provider?.name} · {opportunity.qualification}</p>
              <p className="mt-4 rounded-2xl bg-white p-4 text-sm leading-6 text-slatey">This is an indicative OpenDegree assessment using structured rules, evidence and AI interpretation where needed. It is not an official eligibility, immigration, English or admissions decision.</p>
            </div>

            <div className="mt-8 flex gap-2 overflow-x-auto rounded-3xl border border-slate-200 bg-white p-3">
              {steps.map((label, index) => {
                const enabled = index <= maxStep || Boolean(result);
                return (
                  <button key={label} type="button" onClick={() => enabled && setStep(index)} disabled={!enabled} data-testid={`eligibility-step-link-${index}`}
                    className={`rounded-full px-4 py-2 text-xs font-bold transition-colors ${index === step ? "bg-cobalt text-white" : index < step || result ? "bg-emerald-50 text-emerald-700 hover:bg-emerald-100" : "bg-surface text-slatey"} ${!enabled ? "cursor-not-allowed opacity-50" : "cursor-pointer"}`}>{label}</button>
                );
              })}
            </div>

            <section className="mt-6 rounded-3xl border border-slate-200 bg-white p-6 sm:p-8" data-testid={`eligibility-step-${stepName.toLowerCase()}`}>
              {stepName === "Context" && (
                <div>
                  <h2 className="font-display text-3xl font-bold text-ink">Use information you already have?</h2>
                  <p className="mt-2 max-w-2xl text-sm leading-6 text-slatey">OpenDegree can reuse relevant profile information only if you agree. You remain in control of what is used.</p>
                  {isLearner ? (
                    <div className="mt-6 rounded-3xl border border-slate-200 bg-surface p-5">
                      <h3 className="font-bold text-ink">Information found in your OpenDegree profile</h3>
                      <ul className="mt-3 space-y-2 text-sm text-slatey">
                        {profile?.personal?.first_name && <li>Personal and residence information</li>}
                        {profile?.study_preferences?.subjects && <li>Study preferences</li>}
                        {profile?.international?.english_status && <li>English-language status</li>}
                        <li>Education history, where relevant</li>
                      </ul>
                      <ChoiceGrid testId="profile-use-choice" value={profileChoice} onChange={setProfileChoice} options={[["yes", "Yes, use relevant information"], ["no", "No, answer only what is needed now"]]} />
                    </div>
                  ) : (
                    <div className="mt-6 rounded-3xl border border-cobalt/15 bg-cobalt/5 p-5">
                      <p className="text-sm leading-6 text-slatey">You can run this assessment without an account. Create an account only when you want to save, continue later or apply.</p>
                      <button onClick={() => setProfileChoice("no")} data-testid="guest-continue-button" className="mt-4 rounded-full bg-cobalt px-5 py-3 text-sm font-bold text-white">Continue without an account</button>
                    </div>
                  )}
                </div>
              )}

              {stepName === "Funding" && (
                <div>
                  <h2 className="font-display text-3xl font-bold text-ink">Funding intention</h2>
                  <p className="mt-2 text-sm text-slatey">This tells OpenDegree which structured funding rules to assess.</p>
                  <div className="mt-6"><ChoiceGrid testId="eligibility-funding" value={answers.funding_intention} onChange={(v) => update("funding_intention", v)} options={FUNDING_OPTIONS} /></div>
                </div>
              )}

              {stepName === "Status" && (
                <div className="grid gap-6 md:grid-cols-2">
                  <Field label="Where do you currently live?"><ChoiceGrid testId="eligibility-location" value={answers.current_location} onChange={(v) => update("current_location", v)} options={LOCATION_OPTIONS} /></Field>
                  <Field label="Current UK status" help="Self-declaration only. Share-code verification is a later final step."><Select testId="eligibility-uk-status" value={answers.uk_status} onChange={(v) => update("uk_status", v)} options={UK_STATUS_OPTIONS} placeholder="Select status" /></Field>
                  {opportunity.opportunity_type === "apprenticeship" && <Field label="Right to work in the UK"><ChoiceGrid testId="eligibility-right-work" value={answers.right_to_work} onChange={(v) => update("right_to_work", v)} options={RIGHT_TO_WORK_OPTIONS} /></Field>}
                </div>
              )}

              {stepName === "Education" && (
                <div>
                  <div className="grid gap-5 md:grid-cols-2">
                    <Field label="What best describes your education?"><Select testId="eligibility-education-level" value={answers.highest_education} onChange={(v) => update("highest_education", v)} options={[UNKNOWN_EDUCATION_OPTION, ...EDUCATION_OPTIONS]} placeholder="Select level" /></Field>
                    <Field label="Country of education"><TextInput testId="eligibility-education-country" value={answers.education_country} onChange={(v) => update("education_country", v)} placeholder="Country" /></Field>
                    <Field label="Qualification status"><Select testId="eligibility-award-status" value={answers.award_status} onChange={(v) => update("award_status", v)} options={AWARD_OPTIONS} placeholder="Select status" /></Field>
                    <Field label="English evidence"><Select testId="eligibility-english-status" value={answers.english_status} onChange={(v) => update("english_status", v)} options={ENGLISH_OPTIONS} placeholder="Select evidence" /></Field>
                    <div className="md:col-span-2"><Field label="Describe the study or qualification" help="Use the terms you recognise. Include partial study, missing documents, overseas qualifications or professional evidence."><textarea value={answers.education_description || answers.notes || ""} onChange={(e) => { update("education_description", e.target.value); update("notes", e.target.value); }} data-testid="eligibility-education-description" rows={4} className="w-full rounded-2xl border border-slate-200 p-4 text-sm outline-none focus:border-cobalt" /></Field></div>
                  </div>

                  {answers.education_country && answers.education_country !== "United Kingdom" && (
                    <div className="mt-5 rounded-3xl border border-sky-200 bg-sky-50 p-5" data-testid="eligibility-uk-equivalent-panel">
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <div>
                          <h3 className="flex items-center gap-2 font-display text-xl font-bold text-ink"><Globe2 className="h-5 w-5 text-cobalt" /> Assess UK equivalent</h3>
                          <p className="mt-2 max-w-2xl text-sm leading-6 text-slatey">OpenDegree can provide a potential UK level based on your declared study and evidence. Official recognition/provider confirmation may still be required.</p>
                        </div>
                        <button onClick={assessEducation} disabled={assessingEducation} data-testid="eligibility-assess-uk-equivalent" className="rounded-full bg-cobalt px-4 py-2.5 text-sm font-bold text-white disabled:opacity-50">{assessingEducation ? "Assessing…" : "Assess UK equivalent"}</button>
                      </div>
                      {answers.education_assessment && (
                        <div className="mt-4 rounded-2xl bg-white p-4" data-testid="eligibility-uk-equivalent-result">
                          <p className="font-bold text-cobalt">Potential UK level: {answers.education_assessment.potential_uk_level}</p>
                          <p className="mt-2 text-sm leading-6 text-slatey">{answers.education_assessment.summary}</p>
                          <p className="mt-2 text-xs font-semibold text-amber-700">{answers.education_assessment.official_status}</p>
                        </div>
                      )}
                    </div>
                  )}

                  {answers.english_status === "no_current_evidence" && (
                    <div className="mt-5 rounded-2xl border border-amber-200 bg-amber-50 p-4" data-testid="eligibility-no-english-evidence-action">
                      <p className="text-sm leading-6 text-amber-800"><strong>You don’t currently have English-language evidence.</strong> OpenDegree can assess your English indicatively or continue and identify whether evidence is required later.</p>
                      <button onClick={() => update("english_status", "needs_assessment")} data-testid="eligibility-start-english-assessment" className="mt-3 rounded-full bg-cobalt px-4 py-2.5 text-sm font-bold text-white">Assess English indicatively</button>
                    </div>
                  )}
                  {answers.english_status === "needs_assessment" && <p className="mt-5 rounded-2xl bg-cobalt/5 p-4 text-sm font-semibold text-cobalt" data-testid="eligibility-english-next">Continue to OpenDegree’s indicative English assessment. It is not an official English qualification or SELT.</p>}
                </div>
              )}

              {stepName === "English" && (
                <div>
                  <h2 className="font-display text-3xl font-bold text-ink">Indicative English assessment</h2>
                  <p className="mt-2 text-sm leading-6 text-slatey">This OpenDegree assessment is not an official English qualification or SELT. It provides an estimated level for guidance.</p>
                  <EnglishAssessmentFields answers={answers} update={update} />
                </div>
              )}

              {stepName === "Review" && (
                <div>
                  <h2 className="font-display text-3xl font-bold text-ink">Review before assessment</h2>
                  <div className="mt-6 grid gap-4 md:grid-cols-2">
                    <div className="rounded-2xl bg-surface p-4"><p className="text-xs font-bold uppercase text-slatey">Funding intention</p><p className="mt-1 font-bold text-ink">{optionLabel(FUNDING_OPTIONS, answers.funding_intention)}</p></div>
                    <div className="rounded-2xl bg-surface p-4"><p className="text-xs font-bold uppercase text-slatey">UK status</p><p className="mt-1 font-bold text-ink">{optionLabel(UK_STATUS_OPTIONS, answers.uk_status)}</p></div>
                    <div className="rounded-2xl bg-surface p-4"><p className="text-xs font-bold uppercase text-slatey">Education</p><p className="mt-1 font-bold text-ink">{optionLabel(EDUCATION_OPTIONS, answers.highest_education)} · {optionLabel(AWARD_OPTIONS, answers.award_status)}</p></div>
                    <div className="rounded-2xl bg-surface p-4"><p className="text-xs font-bold uppercase text-slatey">Profile reuse</p><p className="mt-1 font-bold text-ink">{profileChoice === "yes" ? "Allowed" : "Not used"}</p></div>
                  </div>
                </div>
              )}

              {stepName === "Result" && result && (
                <div data-testid="eligibility-result">
                  <div className="rounded-3xl border border-cobalt/15 bg-cobalt/5 p-6">
                    <p className="inline-flex items-center gap-2 rounded-full bg-white px-3 py-1 text-xs font-bold text-cobalt"><Sparkles className="h-4 w-4" /> Indicative assessment</p>
                    <h2 className="mt-4 font-display text-3xl font-bold text-ink" data-testid="eligibility-overall-status">{result.headline}</h2>
                    <p className="mt-2 text-sm font-bold text-ink">Overall: {OVERALL_LABEL[result.overall_status] || statusLabel(result.overall_status)}</p>
                  </div>

                  <div className="mt-6 grid gap-6 lg:grid-cols-[1fr_360px]">
                    <div className="space-y-5">
                      <section className="rounded-3xl border border-slate-200 bg-white p-5">
                        <h3 className="font-display text-2xl font-bold text-ink">Requirement assessment</h3>
                        <ul className="mt-4 space-y-3">
                          {result.requirements.map((item) => (
                            <li key={item.id} data-testid={`eligibility-requirement-${item.id}`} className="rounded-2xl border border-slate-200 p-4">
                              <div className="flex flex-wrap items-center justify-between gap-2"><strong className="text-ink">{item.label}</strong><ResultPill status={item.status} /></div>
                              <p className="mt-2 text-sm leading-6 text-slatey">{item.explanation}</p>
                              {item.next_action && <p className="mt-2 text-sm font-semibold text-ink">Next: {item.next_action}</p>}
                            </li>
                          ))}
                        </ul>
                      </section>

                      {result.ai_assessments?.length > 0 && (
                        <section className="rounded-3xl border border-slate-200 bg-white p-5" data-testid="ai-assessments">
                          <h3 className="font-display text-2xl font-bold text-ink">AI-assisted interpretation</h3>
                          {result.ai_assessments.map((assessment) => (
                            <article key={assessment.assessment_type} className="mt-4 rounded-2xl bg-surface p-4">
                              <div className="flex flex-wrap items-center justify-between gap-2"><h4 className="font-bold capitalize text-ink">{assessment.assessment_type} assessment</h4><span className="rounded-full bg-white px-3 py-1 text-xs font-bold text-slatey">Confidence: {assessment.confidence}</span></div>
                              {assessment.estimated_level && <p className="mt-3 text-lg font-bold text-cobalt">Estimated English level: {assessment.estimated_level}</p>}
                              {assessment.potential_uk_level && <p className="mt-3 text-lg font-bold text-cobalt">Potential UK level: {assessment.potential_uk_level}</p>}
                              <p className="mt-2 text-sm leading-6 text-slatey">{assessment.summary}</p>
                              <p className="mt-3 text-xs font-semibold leading-5 text-amber-700">{assessment.official_status}</p>
                            </article>
                          ))}
                        </section>
                      )}
                    </div>

                    <aside className="space-y-5">
                      <section className="rounded-3xl border border-slate-200 bg-white p-5" data-testid="funding-assessment">
                        <h3 className="font-display text-xl font-bold text-ink">Funding</h3>
                        <p className="mt-3 text-sm text-slatey">Intention: <strong className="text-ink">{optionLabel(FUNDING_OPTIONS, result.funding.intention)}</strong></p>
                        <p className="mt-2"><ResultPill status={result.funding.status} /></p>
                        <p className="mt-3 text-sm leading-6 text-slatey">{result.funding.reason}</p>
                      </section>

                      <section className="rounded-3xl border border-amber-200 bg-amber-50 p-5" data-testid="verification-panel">
                        <h3 className="font-display text-xl font-bold text-ink">{result.verification.title}</h3>
                        <p className="mt-2 text-sm leading-6 text-slatey">{result.verification.message}</p>
                        {result.verification.required ? (
                          <div className="mt-4 space-y-2">
                            <p className="text-sm font-bold text-amber-700">Eligibility: Assessment complete<br />Official verification: Required<br />Application: Waiting for verification</p>
                            <button onClick={startVerification} data-testid="open-verification-button" className="w-full rounded-full bg-cobalt px-4 py-3 text-sm font-bold text-white">Start verification</button>
                            <button onClick={continueLater} data-testid="continue-later-button" className="w-full rounded-full border border-slate-300 bg-white px-4 py-3 text-sm font-bold text-ink">Continue later</button>
                          </div>
                        ) : <p className="mt-3 text-sm font-bold text-emerald-700">No share-code verification indicated from current information.</p>}
                      </section>

                      <section className="rounded-3xl border border-slate-200 bg-white p-5">
                        <h3 className="font-display text-xl font-bold text-ink">What happens next</h3>
                        <ul className="mt-3 space-y-3">
                          {result.route_plan.map((item, index) => <li key={`${item.requirement}-${index}`} className="text-sm leading-6 text-slatey"><strong className="text-ink">{item.requirement}:</strong> {item.next_action}</li>)}
                        </ul>
                        <div className="mt-5 grid gap-2">
                          {!isLearner && <Link to="/app/register?source=eligibility" data-testid="eligibility-create-account" className="rounded-full bg-cobalt px-4 py-3 text-center text-sm font-bold text-white">Create account to save</Link>}
                          {isLearner && <button onClick={saveAssessment} data-testid="save-eligibility-button" className="rounded-full bg-cobalt px-4 py-3 text-sm font-bold text-white">Save assessment</button>}
                          <Link to={`/opportunities/${slug}`} data-testid="eligibility-opportunity-link" className="rounded-full border border-slate-300 px-4 py-3 text-center text-sm font-bold text-ink">Return to opportunity</Link>
                          <RequestCallButton opportunitySlug={slug} sourcePage="eligibility" testId="eligibility-request-call" className="inline-flex items-center justify-center gap-2 rounded-full border border-slate-300 px-4 py-3 text-sm font-bold text-ink" />
                        </div>
                      </section>
                    </aside>
                  </div>
                </div>
              )}

              {error && <p className="mt-5 rounded-2xl bg-rose-50 p-3 text-sm font-bold text-rose-700">{error}</p>}
              {stepName !== "Result" && (
                <div className="mt-8 flex items-center justify-between border-t border-slate-100 pt-5">
                  <button onClick={() => setStep((current) => Math.max(current - 1, 0))} disabled={step === 0 || loading} data-testid="eligibility-back-button" className="inline-flex items-center gap-2 rounded-full border border-slate-300 px-5 py-3 text-sm font-bold text-ink disabled:opacity-40"><ArrowLeft className="h-4 w-4" /> Back</button>
                  <button onClick={next} disabled={!canContinue() || loading} data-testid="eligibility-continue-button" className="inline-flex items-center gap-2 rounded-full bg-cobalt px-6 py-3 text-sm font-bold text-white disabled:opacity-50">{loading && <Loader2 className="h-4 w-4 animate-spin" />} {stepName === "Review" ? "Run assessment" : "Continue"} <ArrowRight className="h-4 w-4" /></button>
                </div>
              )}
            </section>
          </>
        )}
      </main>
      <Footer />
      {verificationOpen && <VerificationModal onClose={() => setVerificationOpen(false)} onContinueLater={async () => { setVerificationOpen(false); await continueLater(); }} />}
    </div>
  );
}
